import React, { useState, useEffect, useMemo } from "react";
import { Code2, Plus, Trash2, Upload, Sparkles, Globe, EyeOff } from "lucide-react";
import QuestionList from "./QuestionList";
import QuestionForm from "./QuestionForm";
import ImportQuestionsModal from "./ImportQuestionsModal";
import GenerateAiCodingModal from "./GenerateAiCodingModal";
import CommonToaster from "../Common/CommonToaster";
import apiClient from "../../utils/apiClient";
import {
  PageHeader, ConfirmModal, AppButton, SearchInput, FilterPanel, FilterField,
  NativeSelect, FilterButton, ActiveFilterChips,
} from "../ui";

const DIFFICULTIES = ["", "Easy", "Medium", "Hard"];
const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "title-asc", label: "Title A → Z" },
  { value: "title-desc", label: "Title Z → A" },
];

const CodingQuestionBankPage = ({ mode = "institute" }) => {
  const isLibrary = mode === "library";
  // AI generation: tenantadmin (platform library) + institute superadmin/admin
  // (own bank, metered against the institute AI token pool). Not instructors —
  // it draws from the shared admin token pool.
  const canUseAi = useMemo(() => {
    if (isLibrary) return true;
    try {
      const usertype = JSON.parse(localStorage.getItem("user") || "{}")?.usertype;
      return ["superadmin", "admin"].includes(usertype);
    } catch {
      return false;
    }
  }, [isLibrary]);
  const [editingQuestion, setEditingQuestion] = useState(null);
  // `published` is a library-only filter (marketplace visibility): "" | "true" | "false".
  const [filters, setFilters] = useState({ category: "", label: "", difficulty: "", search: "", sort: "newest", published: "" });
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filterMeta, setFilterMeta] = useState({ categories: [], labels: [] });
  const [deleteQuestionId, setDeleteQuestionId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Fetch filter options from server
  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/questions/filters");
        setFilterMeta(data);
      } catch { /* silent */ }
    })();
  }, [refreshKey]);

  // Save handler for add/edit (calls API)
  const handleSave = async (question) => {
    try {
      if (question._id) {
        await apiClient.put(`/questions/${question._id}`, question);
      } else {
        await apiClient.post("/questions/add", question);
      }
      setRefreshKey(k => k + 1);
      setEditingQuestion(null);
    } catch (err) {
      setSaveError(err?.response?.data?.message || "Failed to save question. Please try again.");
    }
  };

  // Delete handler
  const handleDelete = (id) => setDeleteQuestionId(id);

  const confirmDelete = async () => {
    const id = deleteQuestionId;
    setDeleteQuestionId(null);
    try {
      await apiClient.delete(`/questions/${id}`);
      setRefreshKey(k => k + 1);
    } catch (err) {
      setSaveError(err?.response?.data?.message || "Failed to delete question.");
    }
  };

  const confirmBulkDelete = async () => {
    const ids = selectedIds;
    setShowBulkDeleteConfirm(false);
    if (ids.length === 0) return;
    try {
      await apiClient.post("/questions/bulk-delete", { ids });
      setSelectedIds([]);
      setRefreshKey(k => k + 1);
    } catch (err) {
      setSaveError(err?.response?.data?.message || "Failed to delete questions.");
    }
  };

  // Bulk marketplace visibility (library mode only).
  const bulkPublish = async (isPublic) => {
    if (selectedIds.length === 0) return;
    setPublishing(true);
    try {
      const { data } = await apiClient.post("/questions/bulk-publish", { ids: selectedIds, isPublic });
      setToast({
        show: true,
        message: data.message || `${selectedIds.length} question(s) ${isPublic ? "published" : "unpublished"}.`,
        type: "success",
      });
      setSelectedIds([]);
      setRefreshKey(k => k + 1);
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || "Failed to update questions.", type: "error" });
    }
    setPublishing(false);
  };

  const handleCancel = () => setEditingQuestion(null);

  const handleResetFilters = () => {
    setFilters(f => ({ ...f, search: "", category: "", label: "", difficulty: "", sort: "newest", published: "" }));
  };

  const activeFilterCount =
    (filters.search ? 1 : 0) + (filters.category ? 1 : 0) + (filters.label ? 1 : 0) + (filters.difficulty ? 1 : 0) + (filters.published ? 1 : 0);

  // Removable chips for the active-filter bar (sort is excluded — it always has
  // a value). Each clears its own field back to the empty default.
  const clearField = (field) => setFilters(f => ({ ...f, [field]: "" }));
  const activeFilters = [
    filters.search && { key: "search", label: `Search: "${filters.search}"`, onRemove: () => clearField("search") },
    filters.category && { key: "category", label: `Category: ${filters.category}`, onRemove: () => clearField("category") },
    filters.label && { key: "label", label: `Label: ${filters.label}`, onRemove: () => clearField("label") },
    filters.difficulty && { key: "difficulty", label: `Difficulty: ${filters.difficulty}`, onRemove: () => clearField("difficulty") },
    isLibrary && filters.published && {
      key: "published",
      label: `Visibility: ${filters.published === "true" ? "Published" : "Not Published"}`,
      onRemove: () => clearField("published"),
    },
  ].filter(Boolean);

  return (
  <>
    <div className="tw-flex tw-flex-col tw-gap-5">
      {saveError && (
        <div className="alert alert-danger py-2 mb-3 small" role="alert">
          {saveError}
          <button type="button" className="btn-close float-end" onClick={() => setSaveError("")} aria-label="Close" />
        </div>
      )}
      {!editingQuestion ? (
        <>
          <PageHeader
            icon={Code2}
            title={isLibrary ? "Platform Coding Questions Library" : "Coding Question Bank"}
            description={isLibrary
              ? "Author coding questions that every institute can use. Mark a question as Public to publish it."
              : "Manage your library of coding interview questions, test cases, and labels"}
          />

          {/* Compact Toolbar */}
          <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
            {/* Filter trigger — opens the panel holding search + all dropdowns. */}
            <FilterButton count={activeFilterCount} onClick={() => setShowFilterModal(true)} />

            {selectedIds.length > 0 && (
              <>
                {/* Marketplace visibility — SaaS-admin library only. */}
                {isLibrary && (
                  <>
                    <AppButton variant="outline" disabled={publishing} onClick={() => bulkPublish(true)}>
                      <Globe size={16} />
                      <span>{publishing ? "Updating…" : `Publish to Marketplace (${selectedIds.length})`}</span>
                    </AppButton>
                    <AppButton variant="outline" disabled={publishing} onClick={() => bulkPublish(false)}>
                      <EyeOff size={16} />
                      <span>Unpublish ({selectedIds.length})</span>
                    </AppButton>
                  </>
                )}
                <AppButton variant="destructive" onClick={() => setShowBulkDeleteConfirm(true)}>
                  <Trash2 size={16} />
                  <span>Delete Selected ({selectedIds.length})</span>
                </AppButton>
              </>
            )}

            {/* Primary actions — pushed to the right. */}
            <div className="tw-flex tw-items-center tw-gap-2 tw-shrink-0 tw-ml-auto">
              <AppButton variant="outline" onClick={() => setShowImportModal(true)}>
                <Upload size={16} />
                <span>Import CSV</span>
              </AppButton>

              {/* AI authoring — SaaS-admin library + institute superadmin/admin. */}
              {canUseAi && (
                <AppButton variant="outline" onClick={() => setShowAiModal(true)}>
                  <Sparkles size={16} />
                  <span>Generate with AI</span>
                </AppButton>
              )}

              <AppButton onClick={() => setEditingQuestion({})}>
                <Plus size={16} />
                <span>Add Question</span>
              </AppButton>
            </div>
          </div>

          {/* ActiveFilterChips carries its own bottom margin for the pages that still
              import SCSS and cannot add a utility. This page is spaced by the shell's
              flex gap, so the margin is cancelled rather than stacked on top of it. */}
          <ActiveFilterChips
            items={activeFilters}
            onClearAll={handleResetFilters}
            className="!tw-mb-0"
          />

          <QuestionList
            filters={filters}
            refreshKey={refreshKey}
            onEdit={setEditingQuestion}
            onDelete={handleDelete}
            mode={mode}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
          />

          {/* Filter Panel — slide-over holding search + all dropdowns (all viewports) */}
          <FilterPanel
            show={showFilterModal}
            onHide={() => setShowFilterModal(false)}
            subtitle="Refine questions to find exactly what you need."
            onReset={handleResetFilters}
            appliedCount={activeFilterCount}
          >
            <FilterField label="Search">
              <SearchInput
                value={filters.search}
                onChange={(val) => setFilters(f => ({ ...f, search: val }))}
                placeholder="Search by title, description or ID..."
              />
            </FilterField>
            <FilterField label="Category">
              <NativeSelect size="lg"
                value={filters.category}
                onChange={(e) => setFilters(f => ({ ...f, category: e.target.value }))}
              >
                <option value="">All Categories</option>
                {filterMeta.categories.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </NativeSelect>
            </FilterField>
            <FilterField label="Label">
              <NativeSelect size="lg"
                value={filters.label}
                onChange={(e) => setFilters(f => ({ ...f, label: e.target.value }))}
              >
                <option value="">All Labels</option>
                {filterMeta.labels.map(l => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </NativeSelect>
            </FilterField>
            <FilterField label="Difficulty">
              <NativeSelect size="lg"
                value={filters.difficulty}
                onChange={(e) => setFilters(f => ({ ...f, difficulty: e.target.value }))}
              >
                {DIFFICULTIES.map((d, idx) => (
                  <option key={d || idx} value={d}>{d || "All Difficulties"}</option>
                ))}
              </NativeSelect>
            </FilterField>
            {isLibrary && (
              <FilterField label="Marketplace Visibility">
                <NativeSelect size="lg"
                  value={filters.published}
                  onChange={(e) => setFilters(f => ({ ...f, published: e.target.value }))}
                >
                  <option value="">All Visibility</option>
                  <option value="true">Published</option>
                  <option value="false">Not Published</option>
                </NativeSelect>
              </FilterField>
            )}
            <FilterField label="Sort By">
              <NativeSelect size="lg"
                value={filters.sort}
                onChange={(e) => setFilters(f => ({ ...f, sort: e.target.value }))}
              >
                {SORT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </NativeSelect>
            </FilterField>
          </FilterPanel>
        </>
      ) : (
        <QuestionForm
          question={editingQuestion._id ? editingQuestion : null}
          onSave={handleSave}
          onCancel={handleCancel}
          mode={mode}
        />
      )}
    </div>

    <ConfirmModal
      show={!!deleteQuestionId}
      onHide={() => setDeleteQuestionId(null)}
      onConfirm={confirmDelete}
      title="Delete Question"
      message="Are you sure you want to delete this question?"
      confirmText="Delete"
      variant="danger"
    />

    <ConfirmModal
      show={showBulkDeleteConfirm}
      onHide={() => setShowBulkDeleteConfirm(false)}
      onConfirm={confirmBulkDelete}
      title="Delete Selected Questions"
      message={`Are you sure you want to delete ${selectedIds.length} question${selectedIds.length === 1 ? "" : "s"}? This cannot be undone.`}
      confirmText="Delete"
      variant="danger"
    />

    <ImportQuestionsModal
      show={showImportModal}
      mode={mode}
      onHide={() => setShowImportModal(false)}
      onImported={() => {
        setShowImportModal(false);
        setRefreshKey((k) => k + 1);
      }}
    />

    {showAiModal && (
      <GenerateAiCodingModal
        mode={mode}
        onClose={() => setShowAiModal(false)}
        onSaved={() => {
          setShowAiModal(false);
          setRefreshKey((k) => k + 1);
        }}
      />
    )}

    <CommonToaster
      show={toast.show}
      message={toast.message}
      type={toast.type}
      onClose={() => setToast({ ...toast, show: false })}
    />
  </>
  );
};

export default CodingQuestionBankPage;
